import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../redux/hooks'
import { selectSearchedTasks, toDateKey, todayKey } from '../../redux/tasks'
import styles from './DashboardHeader.module.css'

export type DashboardHeaderProps = {
  selectedDate: Date
  onSelectDate: (d: Date) => void
}

const formatDashboardDate = (d: Date) =>
  new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  }).format(d)

const formatMonthYear = (d: Date) =>
  new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(d)

const dateDisplayOptions: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
}

const formatShortDate = (iso: string) =>
  new Intl.DateTimeFormat('en-US', dateDisplayOptions).format(new Date(iso))

/** scheduledDate / deadlineDate are yyyy-MM-dd; parse as local calendar day so formatting matches Added/Completed. */
const formatCalendarDateKey = (dateKey: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey.trim())
  if (!m) return formatShortDate(dateKey)
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  return new Intl.DateTimeFormat('en-US', dateDisplayOptions).format(new Date(y, mo - 1, d))
}

const addDays = (d: Date, days: number) => {
  const next = new Date(d)
  next.setDate(d.getDate() + days)
  return next
}

const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1)
const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate()

const SearchIcon = () => (
  <svg
    className={styles.searchIcon}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
  >
    <path
      d="M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15zM21 21l-4.35-4.35"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CaretLeft = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const CaretRight = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect x="3.5" y="5" width="17" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.75" />
    <path d="M3.5 9.5h17" stroke="currentColor" strokeWidth="1.75" />
    <path d="M8 3.5v3M16 3.5v3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
)

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

type CalendarPopoverProps = {
  selected: Date
  onPick: (d: Date) => void
  onClose: () => void
}

const CalendarPopover = ({ selected, onPick, onClose }: CalendarPopoverProps) => {
  const [view, setView] = useState<Date>(() => startOfMonth(selected))
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current) return
      if (!ref.current.contains(e.target as Node)) onClose()
    }
    window.addEventListener('mousedown', onDoc)
    return () => window.removeEventListener('mousedown', onDoc)
  }, [onClose])

  const year = view.getFullYear()
  const month = view.getMonth()
  const firstDow = new Date(year, month, 1).getDay()
  const total = daysInMonth(year, month)
  const todayK = todayKey()
  const selectedK = toDateKey(selected)

  const cells: Array<{ key: string; label: string; date?: Date; muted?: boolean }> = []
  for (let i = 0; i < firstDow; i++) cells.push({ key: `pad-${i}`, label: '', muted: true })
  for (let d = 1; d <= total; d++) {
    const date = new Date(year, month, d)
    cells.push({ key: toDateKey(date), label: String(d), date })
  }

  return (
    <div ref={ref} className={styles.calendarPopover} role="dialog" aria-label="Pick a date">
      <div className={styles.calHead}>
        <button
          type="button"
          className={styles.calNavBtn}
          onClick={() => setView(new Date(year, month - 1, 1))}
          aria-label="Previous month"
        >
          <CaretLeft />
        </button>
        <h4 className={styles.calTitle}>{formatMonthYear(view)}</h4>
        <button
          type="button"
          className={styles.calNavBtn}
          onClick={() => setView(new Date(year, month + 1, 1))}
          aria-label="Next month"
        >
          <CaretRight />
        </button>
      </div>
      <div className={styles.calGrid}>
        {DOW.map((d) => (
          <div key={d} className={styles.calDow}>
            {d}
          </div>
        ))}
        {cells.map((c) => {
          if (!c.date) {
            return <div key={c.key} className={`${styles.calDay} ${styles.calDayMuted}`} />
          }
          const k = toDateKey(c.date)
          const classes = [styles.calDay]
          if (k === todayK) classes.push(styles.calDayToday)
          if (k === selectedK) classes.push(styles.calDaySelected)
          return (
            <button
              key={c.key}
              type="button"
              className={classes.join(' ')}
              onClick={() => onPick(c.date!)}
            >
              {c.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

const DashboardHeader = ({ selectedDate, onSelectDate }: DashboardHeaderProps) => {
  const navigate = useNavigate()
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const [activeIdx, setActiveIdx] = useState(0)
  const searchRef = useRef<HTMLDivElement | null>(null)

  const dateLine = formatDashboardDate(selectedDate)
  const isToday = toDateKey(selectedDate) === todayKey()

  const searchedTasks = useAppSelector(selectSearchedTasks(query))
  const results = useMemo(() => searchedTasks.slice(0, 8), [searchedTasks])

  useEffect(() => {
    if (!searchOpen) return
    const onDoc = (e: MouseEvent) => {
      if (!searchRef.current) return
      if (!searchRef.current.contains(e.target as Node)) setSearchOpen(false)
    }
    window.addEventListener('mousedown', onDoc)
    return () => window.removeEventListener('mousedown', onDoc)
  }, [searchOpen])

  useEffect(() => {
    setActiveIdx(0)
  }, [query])

  const goToTask = (id: string) => {
    setSearchOpen(false)
    setQuery('')
    navigate(`/tasks/${id}`)
  }

  return (
    <header className={styles.bar}>
      <div className={styles.dateGroup}>
        <button
          type="button"
          className={styles.dateNavBtn}
          onClick={() => onSelectDate(addDays(selectedDate, -1))}
          aria-label="Previous day"
        >
          <CaretLeft />
        </button>
        <p className={styles.date} aria-label={dateLine}>
          {dateLine}
          {isToday ? <span className={styles.todayBadge}>Today</span> : null}
        </p>
        <button
          type="button"
          className={styles.dateNavBtn}
          onClick={() => onSelectDate(addDays(selectedDate, 1))}
          aria-label="Next day"
        >
          <CaretRight />
        </button>
        <button
          type="button"
          className={styles.calendarBtn}
          onClick={() => setCalendarOpen((v) => !v)}
          aria-label="Open calendar"
          aria-expanded={calendarOpen}
        >
          <CalendarIcon />
        </button>
        {calendarOpen ? (
          <CalendarPopover
            selected={selectedDate}
            onClose={() => setCalendarOpen(false)}
            onPick={(d) => {
              onSelectDate(d)
              setCalendarOpen(false)
            }}
          />
        ) : null}
      </div>

      <div className={styles.searchSlot} ref={searchRef}>
        <label className={styles.search}>
          <span className={styles.visuallyHidden}>Search tasks</span>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Search tasks..."
            autoComplete="off"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setSearchOpen(true)
            }}
            onFocus={() => setSearchOpen(true)}
            onKeyDown={(e) => {
              if (!searchOpen || results.length === 0) return
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setActiveIdx((i) => Math.min(results.length - 1, i + 1))
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setActiveIdx((i) => Math.max(0, i - 1))
              } else if (e.key === 'Enter') {
                e.preventDefault()
                const pick = results[activeIdx]
                if (pick) goToTask(pick.id)
              } else if (e.key === 'Escape') {
                setSearchOpen(false)
              }
            }}
          />
          <span className={styles.searchIconWrap}>
            <SearchIcon />
          </span>
        </label>
        {searchOpen && query.trim() ? (
          <div className={styles.searchDropdown} role="listbox">
            {results.length === 0 ? (
              <p className={styles.searchEmpty}>
                {query.trim().startsWith('#')
                  ? `No tasks tagged "${query.trim().slice(1).trim()}"`
                  : `No tasks match "${query.trim()}"`}
              </p>
            ) : (
              results.map((t, idx) => {
                const meta = t.completedAt
                  ? `Completed ${formatShortDate(t.completedAt)}`
                  : `Added ${formatShortDate(t.createdAt)}${t.scheduledDate ? ` · Scheduled ${formatCalendarDateKey(t.scheduledDate)}` : ''}${t.deadlineDate ? ` · Deadline ${formatCalendarDateKey(t.deadlineDate)}` : ''}`
                const cls = `${styles.searchResult} ${idx === activeIdx ? styles.searchResultActive : ''}`
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={cls}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => goToTask(t.id)}
                    role="option"
                    aria-selected={idx === activeIdx}
                  >
                    <span className={styles.searchResultTitle}>{t.title}</span>
                    <span className={styles.searchResultMeta}>{meta}</span>
                  </button>
                )
              })
            )}
          </div>
        ) : null}
      </div>
    </header>
  )
}

export default DashboardHeader
